import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Die Playlist-Seite samt ihrer Bearbeitungshälfte.
 *
 * Die Seite trägt gar keine Hinweise mehr. Der letzte zählte die Einträge, die YouTube nicht mehr
 * liefert, und ist ebenfalls weg (2026-08-07): jede dieser Zeilen sagt das bereits selbst, an ihrem
 * Platz und neben der Nummer, die sie erklärt. `entryGone` weiter unten ist diese Beschriftung an der
 * Zeile, und die bleibt.
 */
export const playlist: Strings['playlist'] = {
  entry: {
    positionLabel: (position) => `Eintrag ${position}`,
    upcoming: 'Geplant',
  },

  empty: {
    title: 'Diese Playlist hat keine Videos',
    owned:
      'Es wurde noch nichts hinzugefügt. Ein Video fügt man vom Video aus hinzu: öffne es und nimm „In Playlist speichern“.',
    mirrored:
      'YouTube hat diese Playlist und nichts darin. Eine Playlist, aus der jedes Video entfernt wurde, sieht genauso aus.',
  },

  page: {
    loading: 'Diese Playlist wird geladen…',
    views: (count) => `${count} Aufrufe`,
    updated: (when) => `Aktualisiert ${when}`,
    onThisInstance: 'Auf dieser Instanz',
    playAll: 'Alle abspielen',
    edit: 'Bearbeiten',
    close: 'Schließen',
    entriesLabel: 'Playlist-Einträge',
    order: {
      label: 'Reihenfolge',
      normal: 'Original',
      reversed: 'Umgekehrt',
    },
    remove: (title) => `„${title}“ aus dieser Playlist entfernen`,
    removeTitle: 'Aus dieser Playlist entfernen',
    removing: 'Wird entfernt…',
    entryGone: 'Video nicht verfügbar',
    entryGoneDetail: 'Gelöscht, auf privat gestellt oder aus einem geschlossenen Konto',
    loadMore: (soFar) => `Mehr laden (bisher ${soFar})`,
    loadingMore: 'Wird geladen…',
  },

  manage: {
    enter: 'Verwalten',
    leave: 'Fertig',
    selectLabel: (title) => `${title} auswählen`,
    add: {
      heading: 'Videos hinzufügen',
      label: 'Ein Video zum Hinzufügen finden',
      placeholder: 'Eine Video-URL oder -ID, oder eine Suche',
      clear: 'Feld leeren',
      submit: 'Finden',
      searching: 'Wird gesucht…',
      nothing: (q) => `Kein Video passt zu „${q}“.`,
      notAvailable:
        'Das zeigt auf ein Video, das YouTube nicht liefert, ob gelöscht, privat oder regionsgesperrt, es gibt also nichts hinzuzufügen.',
      action: 'Hinzufügen',
      added: 'Hinzugefügt',
      failed: 'Fehlgeschlagen',
    },
    selectedCount: (count) => `${count} ausgewählt`,
    clearSelection: 'Auswahl aufheben',
    removeSelected: 'Entfernen',
    copySelected: 'Kopieren nach…',
    moveSelected: 'Verschieben nach…',
    working: (done, total) => `Läuft… ${done} von ${total}`,
    failed: {
      title: 'Einige Änderungen wurden abgelehnt',
      detail:
        'Die Instanz hat einen Teil der Änderung nicht angenommen. Die Liste zeigt, was sie jetzt hält, und den Rest auszuwählen und es erneut zu versuchen ist unbedenklich. Ein Video, das YouTube nicht mehr liefert, lässt sich nicht kopieren, nur entfernen.',
      retryable: true,
    },
    transfer: {
      copyTitle: 'In Playlist kopieren',
      moveTitle: 'In Playlist verschieben',
      close: 'Schließen',
      loading: 'Deine Playlists werden geladen…',
      none: 'Noch keine andere Playlist. Die erste ist einen Titel entfernt.',
      copyCreate: 'Anlegen und kopieren',
      moveCreate: 'Anlegen und verschieben',
    },
  },

  editor: {
    sectionLabel: 'Diese Playlist bearbeiten',
    titleLabel: 'Titel',
    descriptionLabel: 'Beschreibung',
    descriptionPlaceholder: 'Wofür diese Playlist da ist',
    privacyLabel: 'Wer sie sehen kann',
    currently: (label, caveat) => `Derzeit ${label}. ${caveat}`,
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    saved: 'Gespeichert',
    delete: 'Playlist löschen',
    confirmDelete: 'Löscht diese Playlist endgültig. Es gibt kein Zurück.',
    deleting: 'Wird gelöscht…',
    noReorder:
      'Einträge bleiben in der Reihenfolge, in der sie hinzugefügt wurden. Einen zu verschieben geht nur, indem man ihn entfernt und neu hinzufügt.',
  },

  problem: {
    notFound: {
      title: 'Keine solche Playlist',
      detail:
        'Entweder existiert sie nicht, oder sie ist privat. Eine private Playlist ist für alle außer ihrem Besitzer unsichtbar, angemeldet oder nicht. „Später ansehen“ und „Mit „Mag ich“ bewertete Videos“ antworten ebenso: sie gehören zu einem Konto und haben keine öffentliche Form.',
      retryable: false,
    },
    unreadable: unreadable('Diese Playlist konnte nicht gelesen werden'),
    upstreamBroken: {
      title: 'Das ist keine Playlist, die Visious öffnen kann',
      detail:
        'YouTube wollte sie nicht liefern. Ein Mix oder eine Radio-Playlist wird für jeden Zuschauer frisch erzeugt statt gespeichert, und dafür gibt es hier keine Seite. Ein neuer Versuch bekommt dieselbe Antwort.',
      retryable: false,
    },
  },
};
